import React, { createContext, useContext, useState, useRef, useCallback, useMemo } from 'react';
import { 
  getVisiblePaths, 
  useMapPointSelect,
  useModeSwitching,
  usePathManagement
} from '../Services';
import { useTpRouteStorage } from '../Services/PathServices/TpRouteStorage';

const TpContext = createContext();
export const useTp = () => {
  const ctx = useContext(TpContext);
  if (!ctx) throw new Error('useTp must be used within a TpProvider');
  return ctx;
};

export const TpProvider = ({ children }) => {
  // Base state
  const [locations, setLocations] = useState([]);
  const [startId, setStartId] = useState('');
  const [visualizationPath, setVisualizationPath] = useState(null);
  const [showPanel, setShowPanel] = useState(true);

  // TP add-point controls (available to sidebar + map)
  const [addPointType, setAddPointType] = useState('supply'); // 'supply' | 'demand'
  const [defaultQuantity, setDefaultQuantity] = useState(10);
  const toggleAddPointType = useCallback(
    () => setAddPointType((t) => (t === 'supply' ? 'demand' : 'supply')),
    []
  );

  // Mode / paths
  const { currentMode, setCurrentMode, currentModeRef } = useModeSwitching();
  const {
    autoPath, setAutoPath, customPath, setCustomPath,
    prevAutoPathRef, prevCustomPathRef, setPath,
    saveAutoPath, saveCustomPath
  } = usePathManagement(locations);

  // Route storage for persistence across mode switches
  const {
    autoRoutes, customRoutes, routeLocations,
    saveAutoRoutes, saveCustomRoutes, getStoredRoutes,
    clearAllRoutes, clearAutoRoutes, clearCustomRoutes
  } = useTpRouteStorage();

  // Map point selection callback
  const { mapPointSelectCallback, setMapPointSelect, clearMapPointSelect } = useMapPointSelect();

  // Map ref
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  const jumpToLocation = useCallback((lat, lng, zoom = 15) => {
    if (!mapRef.current || !mapRef.current.loaded()) return;
    try {
      mapRef.current.flyTo({ center: [lng, lat], zoom, duration: 1000, essential: true });
    } catch (e) {
      console.error('jumpToLocation error:', e);
    }
  }, []);

  const setMapRef = useCallback((map) => { mapRef.current = map; }, []);

  // Path visibility
  const handleUpdateVisiblePath = useCallback((partial) => {
    if (partial === null) setVisualizationPath(null);
    else if (Array.isArray(partial) && partial.length > 0) setVisualizationPath(partial);
    else if (partial && partial.type === 'FeatureCollection') setVisualizationPath(partial);
  }, []);

  const clearAllPaths = useCallback(() => {
    setAutoPath([]); setCustomPath([]); setVisualizationPath(null);
  }, [setAutoPath, setCustomPath]);

  const visiblePaths = useMemo(() => {
    if ((currentMode === 'Auto Path' || currentMode === 'Custom Path') && visualizationPath !== null) {
      return currentMode === 'Auto Path' ? { auto: visualizationPath } : { custom: visualizationPath };
    }
    return getVisiblePaths(currentMode, autoPath, customPath);
  }, [currentMode, autoPath, customPath, visualizationPath]);

  const value = {
    // base
    locations, setLocations, startId, setStartId, showPanel, setShowPanel,
    // mode
    currentMode, setCurrentMode, currentModeRef,
    // TP controls
    addPointType, setAddPointType, toggleAddPointType,
    defaultQuantity, setDefaultQuantity,
    // paths
    autoPath, setAutoPath, customPath, setCustomPath,
    prevAutoPathRef, prevCustomPathRef,
    setPath: (p) => setPath(p, currentMode),
    saveAutoPath, saveCustomPath, clearAllPaths,
    visualizationPath, handleUpdateVisiblePath, visiblePaths,
    // route storage
    autoRoutes, customRoutes, routeLocations,
    saveAutoRoutes, saveCustomRoutes, getStoredRoutes,
    clearAllRoutes, clearAutoRoutes, clearCustomRoutes,
    // map refs
    mapPointSelectCallback, setMapPointSelect, clearMapPointSelect,
    jumpToLocation, mapRef, setMapRef, mapReady, setMapReady
  };

  return <TpContext.Provider value={value}>{children}</TpContext.Provider>;
};
